import type { AxiosRequestConfig } from 'axios';
import z from 'zod';
import { api, getAuthToken } from '../../axios.instance';
import { responseContract } from '../../lib';

export const DashboardInsightTabSchema = z.enum(['OPERATIONAL', 'FINANCIAL']);

export const InsightCategorySchema = z.preprocess(
  // `opportunity` was the third category's name until 2026-09-15. The server
  // rewrites stored rows on read, but backend and frontend deploy separately —
  // if the frontend ships first, rejecting the old name here would blank the
  // whole panel rather than mislabel one card.
  (value) => (value === 'opportunity' ? 'positive' : value),
  z.enum(['priority', 'medium', 'positive'])
);

export const InsightSentimentSchema = z.enum(['negative', 'positive']);

const _PostDashboardInsightRequestDtoSchema = z.object({
  activeDashboard: DashboardInsightTabSchema,
  from: z.string(),
  to: z.string(),
  /** Omit for Portfolio Mode; a single id scopes the insight to one facility. */
  facilitiesIds: z.array(z.number()).optional(),
});

const InsightActionStepSchema = z.object({
  timeframe: z.string(),
  action: z.string(),
});

const InsightBlockSchema = z.object({
  id: z.string(),
  category: InsightCategorySchema,
  /**
   * Derived from the category by the server, so priority/medium always read as
   * warnings and positive as good news. Optional only to survive a frontend
   * deploy that lands ahead of the backend.
   */
  sentiment: InsightSentimentSchema.optional(),
  title: z.string(),
  body: z.string(),
  /** Priority blocks only — the server strips it from the others. */
  why_it_happens: z.string().optional(),
  action_plan: z.array(InsightActionStepSchema).optional(),
  /** Medium and positive blocks only. */
  recommended: z.string().optional(),
});

const PostDashboardInsightResponseDtoSchema = z.object({
  summary: z.string(),
  blocks: z.array(InsightBlockSchema),
  generatedAt: z.string(),
  cached: z.boolean(),
});

/**
 * One server-sent event from the streaming endpoint. `summary` and `block`
 * arrive as the model writes them; `done` carries the final answer — sorted,
 * capped, and authoritative over anything streamed before it.
 */
const DashboardInsightEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('summary'), summary: z.string() }),
  z.object({ type: z.literal('block'), block: InsightBlockSchema }),
  z.object({
    type: z.literal('done'),
    insight: PostDashboardInsightResponseDtoSchema,
  }),
  z.object({ type: z.literal('error'), message: z.string() }),
]);

export type DashboardInsightTab = z.infer<typeof DashboardInsightTabSchema>;
export type InsightCategory = z.infer<typeof InsightCategorySchema>;
export type InsightSentiment = z.infer<typeof InsightSentimentSchema>;
export type InsightActionStep = z.infer<typeof InsightActionStepSchema>;
export type InsightBlock = z.infer<typeof InsightBlockSchema>;

export type PostDashboardInsightRequestDto = z.infer<
  typeof _PostDashboardInsightRequestDtoSchema
>;
export type PostDashboardInsightResponseDto = z.infer<
  typeof PostDashboardInsightResponseDtoSchema
>;
export type DashboardInsightEvent = z.infer<typeof DashboardInsightEventSchema>;

/**
 * POST rather than GET: the selection can carry dozens of facility ids, and the
 * result is generated rather than addressable.
 *
 * Only the filter selection travels — the server reads every metric itself, so
 * the browser never has to have loaded the inactive dashboard.
 */
export const postDashboardInsight = (
  params: PostDashboardInsightRequestDto,
  config?: AxiosRequestConfig<PostDashboardInsightRequestDto>
) =>
  api
    .post('facilities/ai-insight', params, config)
    .then(responseContract(PostDashboardInsightResponseDtoSchema));

/** The stream was refused for an expired session; retry through `api`. */
export class DashboardInsightAuthError extends Error {}

/**
 * The same insight as {@link postDashboardInsight}, delivered card by card.
 *
 * Read with `fetch` rather than `api`: axios in the browser buffers the whole
 * body, which is exactly the wait this exists to remove. The cost is that the
 * token-refresh interceptor does not run here, so an expired session surfaces
 * as {@link DashboardInsightAuthError} and the caller falls back to
 * `postDashboardInsight`, which refreshes.
 *
 * Resolves with the final answer; `onEvent` sees every event on the way.
 */
export const streamDashboardInsight = async (
  params: PostDashboardInsightRequestDto,
  {
    onEvent,
    signal,
  }: {
    onEvent: (event: DashboardInsightEvent) => void;
    signal?: AbortSignal;
  }
): Promise<PostDashboardInsightResponseDto> => {
  const token = getAuthToken();

  const response = await fetch(
    `${api.defaults.baseURL}/facilities/ai-insight/stream`,
    {
      method: 'POST',
      credentials: 'include',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(params),
    }
  );

  if (response.status === 401) throw new DashboardInsightAuthError();
  if (!response.ok || !response.body) {
    throw new Error(`AI insight stream failed with status ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffered = '';

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;

    buffered += decoder.decode(value, { stream: true });

    // Events are separated by a blank line; the last piece may be incomplete.
    const frames = buffered.split('\n\n');
    buffered = frames.pop() ?? '';

    for (const frame of frames) {
      const data = frame
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trim())
        .join('');
      if (!data) continue;

      const event = DashboardInsightEventSchema.parse(JSON.parse(data));
      onEvent(event);

      if (event.type === 'done') return event.insight;
      if (event.type === 'error') throw new Error(event.message);
    }
  }

  throw new Error('AI insight stream ended without an answer');
};
